import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminCatalogProductImage } from '../../../api/types';
import { ProductImageContext, ProductImageUpload, productMediaApi } from './productMediaApi';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function useProductMediaActions(productId: number) {
  const client = useQueryClient();
  const [message, setMessage] = useState('');
  const retryRequests = useRef<ProductImageUpload[]>([]);
  const isRunning = useRef(false);
  const refresh = () => client.invalidateQueries({ queryKey: ['catalog', 'images', productId] });
  const mutation = useMutation({ mutationFn: async (operation: () => Promise<void>) => operation(), onSettled: refresh });
  const run = async (operation: () => Promise<void>, success: string): Promise<void> => {
    if (isRunning.current) return;
    isRunning.current = true;
    setMessage('');
    try { await mutation.mutateAsync(operation); setMessage(success); }
    catch { setMessage(''); }
    finally { isRunning.current = false; }
  };
  const processUploads = async (): Promise<void> => {
    while (retryRequests.current.length > 0) {
      const request = retryRequests.current[0];
      if (!IMAGE_TYPES.includes(request.file.type) || request.file.size > MAX_IMAGE_SIZE || request.file.size === 0) {
        retryRequests.current = [];
        throw new Error('Выберите JPG, PNG или WebP размером до 10 МБ.');
      }
      await productMediaApi.upload(productId, request);
      retryRequests.current.shift();
      await refresh();
    }
  };
  const upload = (files: File[], context: ProductImageContext, cover?: AdminCatalogProductImage): void => {
    if (isRunning.current) return;
    retryRequests.current = files.map((file) => ({ file, context, idempotencyKey: crypto.randomUUID(), linkId: cover?.link_id, revision: cover?.link_image_revision ?? 0 }));
    void run(processUploads, context === 'cover' ? 'Обложка загружена' : 'Фотографии загружены');
  };
  const move = (photos: AdminCatalogProductImage[], index: number, direction: -1 | 1): void => {
    const ids = photos.map((photo) => photo.link_id);
    const nextIds = [...ids];
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [nextIds[index], nextIds[target]] = [nextIds[target], nextIds[index]];
    void run(() => productMediaApi.reorder(productId, nextIds, ids), 'Порядок фотографий сохранён');
  };
  const remove = (photo: AdminCatalogProductImage, context: ProductImageContext): void => {
    if (!window.confirm(context === 'cover' ? 'Убрать обложку товара?' : 'Убрать эту фотографию из товара?')) return;
    void run(() => productMediaApi.remove(productId, context, photo.link_id), 'Фотография удалена из товара');
  };
  return { upload, move, remove, retry: () => { void run(processUploads, 'Фотографии загружены'); }, canRetry: retryRequests.current.length > 0, isPending: mutation.isPending, error: mutation.error, message };
}
